/**
 * 직원 권한 정기 확인 창(T-CONTRACT-1031 §2) — studio·admin-hospital·admin-manufacturer·admin-reseller 같은 내용.
 * 로그인 뒤 탭마다·계정마다 한 번 GET /perm-reviews/status → due 면 창(직원 목록 + 「확인했어요」·「나중에」).
 * 실패(옛 inapi 404·403·5xx)는 조용히 넘어간다(알림 없음). 지원 탭·대리 토큰에서는 묻지 않는다.
 * 「나중에」는 이 탭에서 다시 묻지 않음 — 로그아웃·새 로그인(이 탭·다른 탭)이면 표시를 지워 다음 로그인 때 다시 묻는다.
 * 창의 흐름은 createPermReview 하나(화면은 버튼·이동·storage 이벤트를 넘기기만). Vue·axios 를 들이지 않는 순수 모듈(시험: permReview.test).
 */

export const PERM_REVIEW_STATUS_URL = '/perm-reviews/status';
export const PERM_REVIEW_URL = '/perm-reviews';

export const PERM_REVIEW_TEXT = {
  title: '직원 권한을 확인해 주세요',
  guide: '6개월마다 직원 권한이 맞는지 확인합니다.',
  inactive: '90일 넘게 안 씀',
  confirm: '확인했어요',
  later: '나중에',
};

// 이 탭에서 이미 물은 계정(sessionStorage — 탭마다 따로, 새로 고쳐도 남음)
const ASKED_KEY = 'certi.permReview.asked';

const safe = (fn, fallback) => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

/** JWT 본문(UTF-8). 못 읽으면 null */
const claimsOf = (token) =>
  safe(() => {
    const part = String(token)
      .split('.')[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(part), (c) => c.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes));
    return claims && typeof claims === 'object' ? claims : null;
  }, null);

/**
 * access 토큰(JWT)에서 물을 계정 표시. 앱의 「본인 계정인가」(mfa.isSelfAccount)와 같게 accountId, 없는 옛 토큰은 username.
 * 둘 다 없음·못 읽음·지원(sid)·대리(impersonatedBy) 토큰·skipRoles 역할이면 null(묻지 않음 — 여러 계정이 한 표시를 나눠 쓰지 않게).
 * 서버 판정에 쓰지 않는다 — 이 탭에서 같은 계정에 두 번 묻지 않고, 늦게 온 답을 가려내기 위한 표시.
 */
export const reviewAccountOf = (token, skipRoles = []) => {
  const claims = claimsOf(token);
  if (
    !claims ||
    claims.sid ||
    claims.impersonatedBy ||
    skipRoles.includes(claims.role)
  )
    return null;
  if (typeof claims.accountId === 'string' && claims.accountId)
    return claims.accountId;
  return typeof claims.username === 'string' && claims.username
    ? `username:${claims.username}`
    : null;
};

/**
 * 토큰 주인(로그인 표시). 계정·회사·지원·대리 표시가 같으면 같은 값 — 토큰 갱신은 그대로, 로그아웃은 null.
 * 이 값이 바뀌면(이 탭·다른 탭의 로그아웃·새 로그인·다른 계정) 새 세션으로 본다.
 */
export const loginOwnerOf = (token) => {
  if (!token) return null;
  const c = claimsOf(token);
  return c
    ? [c.accountId, c.username, c.sub, c.sid, c.impersonatedBy]
        .map((v) => (v == null ? '' : String(v)))
        .join('|')
    : '?';
};

/** 이 화면의 로그인 표시 「세대:계정」. 로그인한 화면이 아니면(계정 없음) null */
export const sessionKey = (generation, accountId) =>
  accountId ? `${generation}:${accountId}` : null;

/** 물을 차례인가: 지원 탭 아님 · 계정 있음 · 이 탭에서 이 계정에 아직 안 물음(저장소를 못 쓰면 묻지 않음) */
export const shouldAsk = (storage, accountId, supportTab) =>
  !supportTab &&
  !!accountId &&
  safe(() => storage.getItem(ASKED_KEY) !== accountId, false);

export const markAsked = (storage, accountId) =>
  safe(() => storage.setItem(ASKED_KEY, accountId));

/** 로그아웃·새 로그인한 탭: 다음 로그인 때 다시 묻게 표시를 지움 */
export const forgetAsked = (storage) =>
  safe(() => storage.removeItem(ASKED_KEY));

const pad = (n) => String(n).padStart(2, '0');

/** 마지막 로그인 날짜(이 컴퓨터 시간 YYYY-MM-DD). 없으면 「기록 없음」 */
export const lastLoginText = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    : '기록 없음';
};

/** 켜진 메뉴 → 큰 메뉴 이름만(「CRM > 캠페인」·「CRM > 발송 현황」 → 「CRM」). 하위 메뉴를 다 늘어놓으면 창에서 읽을 수 없음(10-31 intest I14) */
export const bigMenus = (menus) => {
  const names = Array.isArray(menus)
    ? menus.map((m) => String(m).split(' > ')[0].trim()).filter(Boolean)
    : [];
  return names.length ? [...new Set(names)].join(', ') : '-';
};

/** 서버 답 → 창에 그릴 줄. due 가 아니거나 직원이 없으면 null(창 없음) */
export const reviewRows = (data) => {
  const staff =
    data && data.due === true && Array.isArray(data.staff) ? data.staff : [];
  const rows = staff
    .filter((s) => s && s.accountId)
    .map((s) => ({
      accountId: String(s.accountId),
      name: s.name || '-',
      username: s.username || '-',
      lastLogin: lastLoginText(s.lastLoginAt),
      inactive90: s.inactive90 === true,
      menus: bigMenus(s.menus),
    }));
  return rows.length ? rows : null;
};

/** 「확인했어요」 POST /perm-reviews 본문 — 창에 보인 직원만 */
export const confirmBody = (rows) => ({
  accountIds: (rows || []).map((r) => r.accountId),
});

/**
 * 로그인 뒤 한 번 묻기. 차례가 아니면 null. 차례면 표시부터 남기고(실패해도 이 탭에서 다시 부르지 않음) 읽어 줄 또는 null.
 * 답이 오는 사이 로그아웃·다시 로그인(같은 계정도)·다른 계정·로그인 화면으로 갔으면(session() 이 시작 때와 다름) 답을 버린다.
 * @param get () => Promise<GET /perm-reviews/status 본문>
 * @param session () => 지금 화면의 sessionKey(로그인 세대:계정, 로그인한 화면이 아니면 null)
 */
export async function loadPermReview({
  storage,
  accountId,
  supportTab,
  get,
  session,
}) {
  const started = session();
  if (!started || !shouldAsk(storage, accountId, supportTab)) return null;
  markAsked(storage, accountId);
  try {
    const data = await get();
    return session() === started ? reviewRows(data) : null;
  } catch {
    return null;
  }
}

/**
 * 창 하나의 흐름(App.vue 에 하나). state 는 화면의 반응형 { rows, busy }.
 *  check()     화면 이동·active 바뀜 때 — 로그인한 화면이면 탭·계정마다 한 번 묻는다
 *  onStorage(e) 다른 탭의 로그인·로그아웃(storage 이벤트) — 토큰 키가 지워졌거나 주인이 바뀐 이벤트면 지금 토큰이 같은 주인이어도
 *              새 세션(늦게 온 「로그아웃 → 같은 계정 다시 로그인」). 창을 닫고, 로그인한 일반 탭이면 새 세션을 바로 묻는다
 *  later()·confirm() 버튼
 * 로그인 세대는 토큰 주인이 바뀔 때마다(이 탭·다른 탭의 로그아웃·새 로그인) 오르고 토큰 갱신은 그대로.
 * 앞 세션의 늦은 GET·POST 는 새 세션 창을 열거나 닫지 않는다.
 * @param readToken () => 지금 access 토큰(앱 토큰 저장소)
 * @param isActive () => 로그인한 뒤 화면인가(로그인 화면이면 false)
 * @param get () => Promise<GET /perm-reviews/status 본문> · post (body) => Promise
 * @param tokenKeys storage 이벤트로 볼 토큰 키(앱 access 토큰·지원 키)
 */
export function createPermReview({
  state,
  storage,
  readToken,
  isActive,
  isSupportTab,
  get,
  post,
  tokenKeys = [],
  skipRoles = [],
}) {
  let generation = 0;
  let owner; // 마지막으로 본 토큰 주인(undefined = 아직 안 봄)
  let rowsFor = null; // 지금 뜬 목록의 sessionKey
  let postId = 0;

  // 창을 닫고 진행 중 POST 의 뒤처리를 무효로
  const close = () => {
    state.rows = null;
    state.busy = false;
    rowsFor = null;
    postId += 1;
  };

  // 새 세션 — 세대를 올리고 창·busy 를 닫고, forget 이면 다음 로그인 때 다시 묻게 표시를 지움
  const bump = (forget) => {
    owner = loginOwnerOf(readToken());
    generation += 1;
    if (forget) forgetAsked(storage);
    close();
  };

  // 지금 토큰의 주인이 바뀌었으면 새 세션(처음 보는 것은 표시를 지우지 않음 — 새로 고침에 다시 묻지 않게)
  const observe = () => {
    const now = loginOwnerOf(readToken());
    if (now !== owner) bump(owner !== undefined);
  };

  // 다른 탭의 storage 이벤트: 토큰 키가 지워졌거나(로그아웃) 주인이 바뀐 이벤트면 지금 토큰과 관계없이 새 세션
  const onStorage = (event) => {
    const before = generation;
    const key = event ? event.key : undefined;
    const tokenEvent = key === null || tokenKeys.includes(key); // key null = 저장소 전체 지움
    if (
      tokenEvent &&
      (!event.newValue ||
        loginOwnerOf(event.oldValue || null) !==
          loginOwnerOf(event.newValue || null))
    )
      bump(true);
    else observe();
    // 로그인한 일반 탭이면 새 세션을 화면 이동 없이 바로 묻는다
    if (generation !== before && isActive() && !isSupportTab()) void check();
  };

  const sessionNow = () => {
    observe();
    const token = readToken();
    return isActive() && token
      ? sessionKey(generation, reviewAccountOf(token, skipRoles))
      : null;
  };

  async function check() {
    const session = sessionNow();
    // 로그인 화면·다른 세션이면 떠 있던 창을 닫는다(앞 세션 직원 목록을 남기지 않음)
    if (rowsFor !== session) close();
    const token = readToken();
    if (!token) {
      forgetAsked(storage);
      return;
    }
    const rows = await loadPermReview({
      storage,
      accountId: reviewAccountOf(token, skipRoles),
      supportTab: isSupportTab(),
      get,
      session: sessionNow,
    });
    if (rows) {
      state.rows = rows;
      state.busy = false;
      rowsFor = session;
    }
  }

  async function confirm() {
    if (state.busy || !state.rows) return;
    const session = sessionNow();
    // 지금 세션의 목록이 아니면 보내지 않고 닫음
    if (!session || rowsFor !== session) {
      close();
      return;
    }
    const id = ++postId;
    state.busy = true;
    try {
      await post(confirmBody(state.rows));
    } catch {
      // 실패해도 알림 없이 — 기록이 안 됐으면 다음 로그인 때 다시 묻는다
    }
    // 같은 세션·같은 요청일 때만 마무리 — 앞 세션의 늦은 POST 가 새 세션 창을 닫지 않게
    if (id === postId && sessionNow() === session) close();
  }

  return { check, confirm, later: close, onStorage };
}
